import React, { useCallback, useMemo, useRef, useState } from "react";
import { useField } from "formik";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Scrollbars } from "react-custom-scrollbars-2";
import classNames from "classnames";
import { Transaction } from "@common/types/transaction";
import { TransactionRule } from "@common/types/transactionRule";
import { useScope } from "@state/scopeContext.lib";
import { TransactionRuleConditionUpdateDto } from "@common/dto/transactionRule/transaction-rule-condition-update.dto";
import { v4 as uuid } from "uuid";
import { getMatchingTransfersForRule } from "@lib/transferRules";
import {
    useAppliedTransferTransactionsInfiniteQuery,
    useRuleTransactionCounts,
} from "@api/rules.api";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import {
    FormRuleConditionType,
    TransactionRuleConditionFormSchema,
    TransactionRuleFormSchema,
} from "../../TransactionRuleForm/constants";
import { TransferRuleFormSchema } from "../constants";
import { conditionFormRowToModel } from "../../TransactionRuleForm/formToModel.helpers";
import { getDefaultFormRow } from "../../TransactionRuleForm/modelToForm.helpers";
import { RuleTransactionPreview } from "./RuleTransactionPreview";
import {
    TransferPreviewMode,
    TransferRuleTransactionPreviewHeader,
} from "./TransferRuleTransactionPreviewHeader";
import { MatchingPair } from "./MatchingPair";
import { AppliedTransactionRow } from "./AppliedTransactionRow";
import styles from "./RuleTransactionsPreview.module.scss";

const PAGE_SIZE = 50;
const NEXT_PAGE_TRIGGERING_OFFSET = 100;

interface Props {
    rule?: TransactionRule;
    onExpand?: () => void;
    expanded?: boolean;
}

export const TransferRuleTransactionsPreview: React.FC<Props> = ({
    rule,
    onExpand,
    expanded,
}) => {
    const [previewMode, setPreviewMode] =
        useState<TransferPreviewMode>("matching");
    const isEditMode = !!rule;
    const isShowingApplied = previewMode === "applied" && isEditMode;

    const [conditionsField] =
        useField<TransferRuleFormSchema["conditions"]>("conditions");
    const [
        { value: excludedTransactionsIds },
        ,
        { setValue: setExcludedTransactions },
    ] = useField<TransactionRuleFormSchema["excludedTransactionsIds"]>(
        "excludedTransactionsIds",
    );
    const [{ value: applyToExistingTransactions }, ,] = useField<
        TransactionRuleFormSchema["applyToExistingTransactions"]
    >("applyToExistingTransactions");

    const [{ value: excludeMatchedTransfers }, ,] = useField<boolean>(
        "excludeMatchedTransfers",
    );
    const [{ value: matchingMode }] =
        useField<TransferRuleFormSchema["matchingMode"]>("matchingMode");
    const isDisallowMode = matchingMode === "disallow";

    const conditions = useMemo<TransactionRuleConditionFormSchema[]>(() => {
        const dateProximityRow: TransactionRuleConditionFormSchema | null =
            conditionsField.value.dateProximity !== undefined
                ? {
                      ...getDefaultFormRow(),
                      id: uuid(),
                      conditionType: FormRuleConditionType.DATE_PROXIMITY,
                      dateProximity: conditionsField.value.dateProximity,
                  }
                : null;

        return [
            ...conditionsField.value.from,
            ...conditionsField.value.to,
            ...(dateProximityRow ? [dateProximityRow] : []),
        ];
    }, [conditionsField.value]);

    const { workspaceId } = useScope();
    const noConditions = conditions.length < 2;
    const matchingTransactions = useInfiniteQuery({
        queryKey: [
            "rulesMatchingTransactions",
            workspaceId,
            conditions,
            rule?.id,
            excludeMatchedTransfers,
            matchingMode,
        ],
        queryFn: async ({ pageParam }) => {
            if (!workspaceId || noConditions) {
                return;
            }

            const { page, fetchedTransactionsIds } = pageParam;

            const conditionsToSend: TransactionRuleConditionUpdateDto[] =
                conditions
                    .map(conditionFormRowToModel)
                    .filter(
                        (
                            v,
                        ): v is NonNullable<
                            ReturnType<typeof conditionFormRowToModel>
                        > => v !== undefined,
                    );

            const result = await getMatchingTransfersForRule(workspaceId, {
                conditions: conditionsToSend,
                page,
                limit: PAGE_SIZE,
                // In disallow mode the preview is meant to show currently-paired
                // transfers that will be unmatched, so the "exclude matched"
                // toggle is not meaningful — force it off for the backend.
                excludeMatchedTransfers: isDisallowMode
                    ? false
                    : excludeMatchedTransfers,
                excludedTransactionsIds: [
                    ...(excludedTransactionsIds ?? []),
                    ...fetchedTransactionsIds,
                ],
                matchingMode,
            });

            return result.matches;
        },
        initialPageParam: {
            page: 0,
            fetchedTransactionsIds: [] as number[],
        },
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage || lastPage.length !== PAGE_SIZE) {
                return undefined;
            }

            const fetchedTransactionsIds = pages.flatMap(
                (page) => page?.map(({ match }) => match.id) ?? [],
            );

            return { page: pages.length, fetchedTransactionsIds };
        },
        enabled: !isShowingApplied,
    });

    const appliedTransfers = useAppliedTransferTransactionsInfiniteQuery({
        ruleId: rule?.id,
        enabled: isShowingApplied,
        pageSize: PAGE_SIZE,
    });

    const transactionCounts = useRuleTransactionCounts();
    const appliedCount = rule ? (transactionCounts.data?.[rule.id] ?? 0) : 0;

    const matchingTotal = matchingTransactions.data?.pages.flatMap(
        (page) => page?.map(({ match }) => match.id) ?? [],
    ).length;
    const noMatchingTransactions = matchingTotal === 0;

    const appliedAllowPairs = useMemo(() => {
        if (!isShowingApplied) {
            return [];
        }
        return (
            appliedTransfers.data?.pages.flatMap((page) =>
                page?.mode === "allow" ? page.matches : [],
            ) ?? []
        );
    }, [appliedTransfers.data?.pages, isShowingApplied]);

    const appliedDisallowTransactions = useMemo(() => {
        if (!isShowingApplied) {
            return [];
        }
        return (
            appliedTransfers.data?.pages.flatMap((page) =>
                page?.mode === "disallow" ? page.transactions : [],
            ) ?? []
        );
    }, [appliedTransfers.data?.pages, isShowingApplied]);

    const appliedFlatCount =
        matchingMode === "allow"
            ? appliedAllowPairs.length
            : appliedDisallowTransactions.length;
    const noAppliedTransactions = isShowingApplied && appliedFlatCount === 0;

    const toggleTransaction = useCallback(
        (transaction: Transaction) => {
            if (excludedTransactionsIds.includes(transaction.id)) {
                void setExcludedTransactions(
                    excludedTransactionsIds.filter(
                        (id) => id !== transaction.id,
                    ),
                );
            } else {
                void setExcludedTransactions([
                    ...excludedTransactionsIds,
                    transaction.id,
                ]);
            }
        },
        [excludedTransactionsIds, setExcludedTransactions],
    );

    const scrollbars = useRef<Scrollbars>(null);
    const onScroll = useCallback(() => {
        if (!scrollbars.current) {
            return;
        }
        const scrollTop = scrollbars.current.getScrollTop();
        const scrollHeight = scrollbars.current.getScrollHeight();
        const clientHeight = scrollbars.current.getClientHeight();

        const shouldFetchNext =
            scrollTop + clientHeight >=
            scrollHeight - NEXT_PAGE_TRIGGERING_OFFSET;

        if (!shouldFetchNext) {
            return;
        }

        if (isShowingApplied) {
            if (
                appliedTransfers.hasNextPage &&
                !appliedTransfers.isFetchingNextPage
            ) {
                void appliedTransfers.fetchNextPage();
            }
            return;
        }

        if (
            matchingTransactions.hasNextPage &&
            !matchingTransactions.isFetchingNextPage
        ) {
            void matchingTransactions.fetchNextPage();
        }
    }, [appliedTransfers, isShowingApplied, matchingTransactions]);

    const hasTableRows =
        !!matchingTransactions.data?.pages.at(0)?.length ||
        (isShowingApplied && appliedFlatCount > 0);

    const renderMatchingContent = () => (
        <>
            {matchingTransactions.data?.pages.map((page) =>
                page?.map(({ transaction, match }) => (
                    <RuleTransactionPreview
                        key={transaction.id}
                        transaction={transaction}
                        pairedTransaction={match}
                        toggleTransaction={toggleTransaction}
                        applyToExistingTransactions={
                            applyToExistingTransactions
                        }
                        isSelected={
                            !excludedTransactionsIds.includes(transaction.id)
                        }
                    />
                )),
            )}
            {noConditions && (
                <EmptyState header="Add conditions to view impacted transactions" />
            )}
            {noMatchingTransactions && (
                <EmptyState
                    header="No transfers match selected conditions."
                    body="You can still create this rule and apply it going forward."
                />
            )}
        </>
    );

    const appliedEmptyHeader =
        matchingMode === "allow"
            ? "This rule has not matched any transfers yet."
            : "This rule has not disallowed any transfers yet.";

    const renderAppliedContent = () => {
        if (matchingMode === "allow") {
            return (
                <>
                    {appliedAllowPairs.map(({ transaction, match }) => (
                        <div
                            key={transaction.id}
                            className={classNames(
                                styles.row,
                                styles.bodyRow,
                                styles.readOnlyPair,
                            )}
                        >
                            <MatchingPair
                                transaction={transaction}
                                pairedTransaction={match}
                                isChecked
                                isDisabled
                                toggle={() => undefined}
                            />
                        </div>
                    ))}
                    {noAppliedTransactions && (
                        <EmptyState header={appliedEmptyHeader} />
                    )}
                </>
            );
        }

        return (
            <>
                {appliedDisallowTransactions.map((transaction) => (
                    <AppliedTransactionRow
                        key={transaction.id}
                        transaction={transaction}
                    />
                ))}
                {noAppliedTransactions && (
                    <EmptyState header={appliedEmptyHeader} />
                )}
            </>
        );
    };

    const activeQuery = isShowingApplied
        ? appliedTransfers
        : matchingTransactions;

    return (
        <div className={styles.transactionsContainer}>
            <TransferRuleTransactionPreviewHeader
                totalTransactions={matchingTotal ?? 0}
                onExpand={onExpand}
                expanded={expanded}
                isEditMode={isEditMode}
                previewMode={previewMode}
                onPreviewModeChange={setPreviewMode}
                appliedCount={appliedCount}
            />
            <Scrollbars
                ref={scrollbars}
                onScroll={onScroll}
                style={{ height: "100%" }}
            >
                <div
                    className={classNames(styles.table, {
                        [styles.hasRows]: hasTableRows,
                    })}
                >
                    {isShowingApplied
                        ? renderAppliedContent()
                        : renderMatchingContent()}
                </div>
                {(activeQuery.hasNextPage || activeQuery.isLoading) && (
                    <Loader />
                )}
            </Scrollbars>
        </div>
    );
};
